/**
 * The maths every figure on the cost surfaces shares (spec 076).
 *
 * **This module imports nothing.** That is deliberate and load-bearing: it is
 * consumed by `Breakdown`, which renders eagerly on the usage page, so anything
 * imported here reaches the entry bundle. `@visx/*` therefore lives inside the
 * lazy boundary with the axis that needs it, not in this file — see
 * `check-bundle.sh`, which asserts the boundary by name rather than trusting it.
 *
 * The design document put `axisTicks` here too. It cannot live here for the
 * reason above; it sits with the axis instead.
 */

/**
 * The one sanctioned conversion of money to a number.
 *
 * Named, exported and used everywhere rather than inlining `Number(...)`, so a
 * reviewer can grep for any *other* conversion and find it — which is the whole
 * enforcement mechanism for spec 076 FR-028.
 *
 * Why a conversion is permitted at all: a bar's height is a different kind of
 * quantity from a printed amount. Nobody can read a rounding error off a
 * rectangle, and no amount of precision changes which pixel a bar ends on. What
 * must never happen is the number travelling back to a *label* — the service
 * serialises `NUMERIC(24,20)` exactly, `money.ts` already carries a fix for
 * `Math.floor(value * 100)` rendering `$19.99` as `$19.98`, and `exactMoney` is
 * string surgery precisely to keep that guarantee.
 *
 * So: this value sizes a shape. `formatMoney` and `exactMoney` print the money.
 * Both come from the same entry, and they are not interchangeable.
 *
 * A value that is not a number sizes nothing, which is why the fallback is zero
 * rather than an error — a malformed amount should draw no bar, not blank the
 * figure. That is *not* the "absence is not zero" rule from spec 074, which
 * governs what a reader is *told*; this governs what is *drawn*, and an unknown
 * length is no length.
 */
export function magnitude(amount: string): number {
  const value = Number(amount);
  return Number.isFinite(value) ? value : 0;
}

/**
 * The narrowest a day label may be drawn before labels start colliding.
 *
 * Two digits of Ubuntu at the axis size measure about 16px; the rest is the gap
 * that keeps `30` and `31` from reading as one number.
 */
const MIN_LABEL_PX = 28;

/**
 * How many days to skip between axis labels so they never overlap.
 *
 * Returns 1 when every day fits, 2 for every other day, and so on. A stated
 * rule, because the alternative — letting 31 labels overlap and calling it
 * responsive — is the defect this exists to prevent, and it is the kind that
 * only appears at one window width nobody tested.
 *
 * Deliberately not "always show the 1st, 8th, 15th…": a fixed set breaks at
 * both ends. A short period in progress has too few days to need thinning at
 * all, and a narrow phone viewport needs more thinning than a fixed set gives.
 */
export function labelStride(dayCount: number, widthPx: number): number {
  if (dayCount <= 0 || widthPx <= 0) return 1;

  const perDay = widthPx / dayCount;
  if (perDay >= MIN_LABEL_PX) return 1;

  return Math.ceil(MIN_LABEL_PX / perDay);
}

/**
 * Whether a day at this position gets a label, given the stride.
 *
 * The first day always keeps its label. A figure whose axis begins unlabelled
 * reads as though it starts somewhere other than where it does.
 */
export function labelledAt(index: number, stride: number): boolean {
  return index % stride === 0;
}
